import { gql } from '@apollo/client'
import { BodyText, Heading, RadioCard } from '@farol-ds/react'
import { useEffect, useState } from 'react'

import { CheckoutButton } from '../checkout-button'
import { OneClickButton } from '../one-click-button'
import {
  ThirdPartyBackGroundCheckContent_OfferLockDataFragment,
  ThirdPartyBackGroundCheckContent_ViewerFragment,
} from './__generated__/third-party-background-check-content.graphql'
import PlanOfferCards from './plan-offer-cards'
import SinglePlanCard from './single-plan-card'
import styles from './third-party-background-check-lock.module.scss'

export interface ThirdPartyBackGroundCheckContentProps {
  offerLockData: ThirdPartyBackGroundCheckContent_OfferLockDataFragment
  viewer: ThirdPartyBackGroundCheckContent_ViewerFragment
}

type Card = ThirdPartyBackGroundCheckContent_OfferLockDataFragment['body']['cards'][number]

export function ThirdPartyBackGroundCheckContent({
  offerLockData,
  viewer,
}: ThirdPartyBackGroundCheckContentProps) {
  const header = offerLockData.header
  const { title = '', subtitle = '' } = header
  const { body } = offerLockData || {}
  const { cards } = body || {}
  const [selectedCard, setSelectedCard] = useState<Card | null>(null)
  const hasSingleCard = cards.length === 1

  useEffect(() => {
    if (hasSingleCard && cards[0]) {
      setSelectedCard(cards[0])
    }
  }, [hasSingleCard, cards])

  return (
    <>
      <Heading size="lg" className={styles.thirdPartyBackgroundCheckLockTitle}>
        {title}
      </Heading>
      <BodyText size="md" className={styles.thirdPartyBackgroundCheckLockSubtitle}>
        <span dangerouslySetInnerHTML={{ __html: `${subtitle}` }} />
      </BodyText>
      {hasSingleCard ? (
        <SinglePlanCard offerLockData={offerLockData} />
      ) : (
        <div className={styles.thirdPartyBackgroundCheckLockCards}>
          <RadioCard.Group
            defaultValue=""
            name="third-party-background-check-lock-group"
            onValueChange={(value) => setSelectedCard(cards[Number(value)])}
            className={styles.thirdPartyBackgroundCheckLockRadioCardGroup}
          >
            <PlanOfferCards offerLockData={offerLockData} />
          </RadioCard.Group>
        </div>
      )}
      <div className={styles.thirdPartyBackgroundCheckLockPaymentButtons}>
        <CheckoutButton
          forceDisabled={!selectedCard}
          offerLockCard={selectedCard || cards[0]}
          kind="primary"
        />
        <OneClickButton
          forceDisabled={!selectedCard}
          offerLockCard={selectedCard || cards[0]}
          viewer={viewer}
          kind="primary"
        />
      </div>
    </>
  )
}

ThirdPartyBackGroundCheckContent.fragments = {
  offerLockData: gql`
    fragment ThirdPartyBackGroundCheckContent_offerLockData on OfferLockData {
      header {
        title
        subtitle
      }
      ...PlanOfferCards_offerLockData
      ...SinglePlanCard_offerLockData
      body {
        cards {
          ...OneClickButton_offerLockCard
          ...CheckoutButton_offerLockCard
        }
      }
    }
    ${PlanOfferCards.fragments.offerLockData}
    ${SinglePlanCard.fragments.offerLockData}
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment ThirdPartyBackGroundCheckContent_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
